import type { CityConfig } from "@/pages/city-template/types";

export const lebanonPaConfig: CityConfig = {
  city: "Lebanon",
  state: "Pennsylvania",
  stateAbbr: "PA",
  slug: "lebanon-pa",
  route: "/cities/lebanon-pa",
  title: "Emergency Passport Lebanon, PA — Expedited Same-Day Service | Fast Passport Center",
  metaDescription: "Emergency and expedited passport services in Lebanon, PA — as fast as 24 hours. Same-day courier processing available for Lebanon County travelers, near Hershey and Harrisburg. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Lebanon, Pennsylvania",
  heroSubtitle: "Family trip planned and realized the kids need passports? Heading overseas from Central PA on short notice? Fast Passport Center helps Lebanon and Lebanon County residents get passports fast — as fast as 24 hours. A+ BBB rated. No office visit required for most services.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Lebanon%20Pennsylvania%20historic%20downtown%20Main%20Street%20with%20classic%20brick%20storefronts%20and%20church%20steeples%2C%20Lebanon%20County%20landscape%20with%20rolling%20farmland%20and%20gentle%20hills%2C%20small%20Pennsylvania%20Dutch%20country%20town%20atmosphere%2C%20warm%20afternoon%20golden%20hour%20lighting%2C%20quaint%20American%20small%20town%20charm%2C%20photorealistic%20aerial%20neighborhood%20photography%2C%20no%20readable%20text%20or%20signs&width=1800&height=700&seq=lebanon-pa-hero-01&orientation=landscape",
  heroImageAlt: "Lebanon Pennsylvania historic downtown Main Street with Lebanon County landscape",
  geoPosition: "40.3409;-76.4113",
  geoPlacename: "Lebanon, Pennsylvania",
  lastModified: "2026-06-06",
  regionalAgency: {
    name: "Philadelphia Passport Agency",
    address: "200 Chestnut St, Philadelphia, PA 19106",
    distance: "About 95 miles east — 1.75 hours by car",
  },
  nearbyCitiesZips: [
    { name: "Lebanon", zips: "17042, 17046" },
    { name: "Palmyra", zips: "17078" },
    { name: "Annville", zips: "17003" },
    { name: "Myerstown", zips: "17067" },
    { name: "Cleona", zips: "17042" },
    { name: "Cornwall", zips: "17016" },
    { name: "Jonestown", zips: "17038" },
    { name: "Newmanstown", zips: "17073" },
    { name: "Schaefferstown", zips: "17088" },
    { name: "Fredericksburg", zips: "17026" },
    { name: "Richland", zips: "17087" },
    { name: "Campbelltown", zips: "17010" },
  ],
  benefits: [
    { icon: "ri-computer-line", title: "Start 100% Online", desc: "No office visit required to begin. Complete your order from home in Lebanon or the Lebanon Valley area in minutes." },
    { icon: "ri-truck-line", title: "Ship Overnight from Lebanon County", desc: "FedEx and UPS locations throughout Lebanon and the Lebanon Valley offer overnight shipping to our processing center." },
    { icon: "ri-shield-check-line", title: "Expert Document Review", desc: "Every application is reviewed by our team before submission to reduce errors and delays — critical for Lebanon families with upcoming travel." },
    { icon: "ri-calendar-check-line", title: "Reserved Agency Access", desc: "We hold reserved submission slots at the Philadelphia Passport Agency — no public appointment hunting needed." },
  ],
  testimonials: [
    {
      name: "Nancy W.",
      location: "Lebanon, PA",
      rating: 5,
      text: "We planned a family trip to Italy and realized my husband's passport was expiring in two months. Fast Passport Center got it renewed in 5 days. We shipped from the UPS Store on Cumberland Street. So relieved we didn't have to cancel the trip.",
      service: "Passport Renewal",
    },
    {
      name: "Mark D.",
      location: "Palmyra, PA",
      rating: 5,
      text: "My daughter needed a passport for a school trip to France with only three weeks to go. Fast Passport Center walked us through everything and her passport arrived in 7 days. The checklist made it so simple.",
      service: "New Passport",
    },
    {
      name: "Karen S.",
      location: "Annville, PA",
      rating: 5,
      text: "Lost my passport two weeks before a business trip to London. I was panicking. Fast Passport Center helped me with the lost passport replacement and I had a new one in 8 days. Incredible turnaround.",
      service: "Lost Passport Replacement",
    },
  ],
  facilities: [
    {
      id: "lebanon-post",
      name: "Lebanon Post Office",
      type: "USPS Passport Acceptance",
      address: "101 S 8th St",
      city: "Lebanon",
      state: "PA",
      zip: "17042",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm",
      distance: "Downtown Lebanon",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Primary passport acceptance location in Lebanon. Appointment required — book online well in advance. Photo services available on-site.",
      icon: "ri-mail-line",
    },
    {
      id: "palmyra-post",
      name: "Palmyra Post Office",
      type: "USPS Passport Acceptance",
      address: "22 S Railroad St",
      city: "Palmyra",
      state: "PA",
      zip: "17078",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Palmyra — about 5 miles east",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Palmyra option for eastern Lebanon County residents, close to Hershey.",
      icon: "ri-mail-line",
    },
    {
      id: "hershey-post",
      name: "Hershey Post Office",
      type: "USPS Passport Acceptance",
      address: "151 W Chocolate Ave",
      city: "Hershey",
      state: "PA",
      zip: "17033",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm",
      distance: "Hershey — about 12 miles southwest",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Hershey option for southern Lebanon County and Dauphin County border residents.",
      icon: "ri-mail-line",
    },
    {
      id: "lebanon-county-courthouse",
      name: "Lebanon County Courthouse",
      type: "County Passport Acceptance",
      address: "400 S 8th St",
      city: "Lebanon",
      state: "PA",
      zip: "17042",
      phone: "717-228-4400",
      hours: "Mon–Fri 8:30am–4:30pm",
      distance: "Downtown Lebanon",
      website: "https://www.lebcounty.org/",
      websiteLabel: "Lebanon County Website",
      notes: "County courthouse may offer passport acceptance. Call ahead to confirm hours and appointment requirements.",
      icon: "ri-government-line",
    },
  ],
  faqs: [
    {
      q: "Can I get an emergency passport in Lebanon before international travel?",
      a: "Yes. Through Fast Passport Center, Lebanon travelers can get an expedited passport in as little as 2–9 business days, and emergency processing is available in as fast as 24 hours depending on your eligibility and travel documentation. Start your order online today.",
    },
    {
      q: "How fast can I renew my passport in Lebanon?",
      a: "Fast passport renewal in Lebanon is available through Fast Passport Center in as little as 3 business days with our expedited service, or 3–5 weeks with our standard expedited option. Renewal applications are eligible if your current passport was issued at age 16 or older and within the last 15 years.",
    },
    {
      q: "Is Lebanon close enough to use the Philadelphia Passport Agency?",
      a: "Lebanon is about 95 miles from the Philadelphia Passport Agency — roughly a 1.75-hour drive each way. The agency requires appointments and proof of travel within 14 days. Fast Passport Center is the more convenient option: start online and ship from your local Lebanon FedEx or UPS.",
    },
    {
      q: "Does Lebanon have a regional passport agency?",
      a: "Lebanon does not have a regional passport agency. The closest is the Philadelphia Passport Agency (about 95 miles east). Both require appointments and proof of travel within 14 days. Fast Passport Center is the faster, more convenient solution for most Lebanon County residents.",
    },
    {
      q: "Can I expedite a child passport in Lebanon?",
      a: "Yes. Child passport expediting in Lebanon is possible through Fast Passport Center. Child passports for minors 15 and under require an in-person acceptance step with parental consent. We provide a clear checklist. Once the acceptance step is complete, we handle expedited processing.",
    },
    {
      q: "What airports do Lebanon travelers typically use?",
      a: "Harrisburg International Airport (MDT — about 30 miles southwest) is the closest commercial airport for Lebanon travelers. Philadelphia International Airport (PHL — about 90 miles east) offers extensive international flights. Both are easily accessible from the Lebanon Valley.",
    },
    {
      q: "How long does a standard passport take in Lebanon?",
      a: "If you apply through a standard post office acceptance facility in Lebanon, processing times run 6–13 weeks — sometimes longer during peak travel season. If your travel is sooner, you need expedited processing through Fast Passport Center.",
    },
    {
      q: "Can I get a passport renewal in Lebanon without visiting an office?",
      a: "Yes. Most Lebanon-area adults renewing a passport can do so entirely by mail — no in-person visit required. Start your order online, complete documents at home, and ship overnight from any FedEx or UPS location in Lebanon or the Lebanon County area.",
    },
  ],
  photoLocations: [
    { name: "Walgreens — Lebanon", address: "1700 Cumberland St, Lebanon, PA 17042", hours: "Daily 8 AM – 10 PM" },
    { name: "CVS — Lebanon", address: "1350 Cumberland St, Lebanon, PA 17042", hours: "Daily 8 AM – 9 PM" },
    { name: "Walmart — Lebanon", address: "1355 E Lehman St, Lebanon, PA 17046", hours: "Daily 6 AM – 11 PM" },
    { name: "The UPS Store — Lebanon", address: "2200 Cumberland St, Lebanon, PA 17042", hours: "Mon–Fri 8 AM – 7 PM, Sat 9 AM – 3 PM" },
    { name: "FedEx Office — Harrisburg", address: "4601 Jonestown Rd, Harrisburg, PA 17109", hours: "Mon–Fri 7 AM – 10 PM" },
  ],
  dmvLocations: [
    {
      name: "Lebanon PennDOT Driver License Center",
      address: "900 E Cumberland St, Lebanon, PA 17042",
      hours: "Tue–Sat 8:30 AM – 4:15 PM",
      note: "Lebanon PennDOT center. Driver licenses, REAL ID, ID cards. Appointment recommended.",
      featured: true,
    },
    {
      name: "Harrisburg PennDOT Driver License Center",
      address: "1101 S Front St, Harrisburg, PA 17104",
      hours: "Tue–Sat 8:30 AM – 4:15 PM",
      note: "Harrisburg PennDOT option for southern Lebanon County residents.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "MDT",
      name: "Harrisburg International Airport",
      distance: "About 30 miles southwest — 35 minutes drive",
      address: "1 Terminal Dr, Middletown, PA 17057",
      note: "MDT is the closest commercial airport for Lebanon travelers, offering connections to major hubs for international travel. Passport required for all international connections.",
      flightUrl: "https://www.flyhia.com/",
      flightLabel: "MDT Airport Website",
      featured: true,
    },
    {
      code: "PHL",
      name: "Philadelphia International Airport",
      distance: "About 90 miles east — 1.5 hours drive",
      address: "8000 Essington Ave, Philadelphia, PA 19153",
      note: "PHL offers extensive domestic and international flights and is a convenient option for Lebanon travelers with direct international itineraries.",
      flightUrl: "https://www.phl.org/",
      flightLabel: "PHL Airport Website",
      featured: false,
    },
  ],
  services: [
    {
      icon: "ri-time-line",
      title: "Emergency Passport",
      description: "Flying from MDT or PHL in 24–72 hours? Our emergency courier service is designed for urgent, last-minute travel from Lebanon and Central Pennsylvania.",
      bullets: ["As fast as 24 hours", "Reserved agency submission slots", "Live case tracking"],
      badge: "Same-Day Available",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Passport Renewal",
      description: "Already have a U.S. passport? We help Lebanon and Lebanon County travelers renew fast — no office visit required for most renewals.",
      bullets: ["Eligible for expedited processing", "Ship from any FedEx or UPS in Lebanon", "Professional document review"],
      badge: "Most Common",
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-search-eye-line",
      title: "Lost Passport Replacement",
      description: "Lost your passport before a trip? We guide Lebanon travelers through Form DS-64 and the replacement process — urgent timelines available.",
      bullets: ["DS-64 form guidance included", "Replacement processing", "Urgent timelines available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Child passport expedited service for Lebanon families — minors under 16 require in-person acceptance and parental consent.",
      bullets: ["Both parents must typically consent", "Clear checklist provided", "Expedited options after acceptance step"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-add-line",
      title: "New Passport",
      description: "First-time passport application assistance for Lebanon residents and Lebanon Valley travelers.",
      bullets: ["Step-by-step checklist", "Acceptance clerk guidance", "Expedited after clerk visit"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-truck-line",
      title: "Passport Courier Service",
      description: "Fast Passport Center is a registered U.S. Department of State courier — we submit through reserved agency channels, not public lines.",
      bullets: ["State Dept. registered courier", "Reserved daily submission slots", "Not affected by government shutdowns"],
      badge: "Registered Courier",
      link: "https://fastpassportcenter.com/checkout/",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change & Other Services",
      description: "Passport name changes after marriage or divorce, damaged passport replacement, second passports for frequent travelers — all expedited.",
      bullets: ["Name change after marriage/divorce", "Damaged passport replacement", "Second passport for frequent travel"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for Lebanon, Pennsylvania travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Expedited Passport", description: "Expedited passport processing for Lebanon and Lebanon County residents needing passports faster than standard government mail timelines." },
    { name: "Passport Renewal", description: "Fast passport renewal for Lebanon, PA residents with a passport issued within the last 15 years at age 16 or older." },
    { name: "Lost Passport Replacement", description: "Emergency passport replacement service for Lebanon travelers with lost or stolen passports." },
    { name: "Child Passport", description: "Expedited child passport services for Lebanon families with minors under 16." },
    { name: "Passport Courier Service", description: "Registered U.S. Department of State courier passport submission service serving Lebanon, Pennsylvania." },
  ],
  mistakes: [
    { icon: "ri-time-line", title: "Waiting Too Long to Apply", description: "Lebanon travelers often assume 6–8 weeks is safe — it's not. Government processing has stretched to 10–14+ weeks during peak travel season. Apply as soon as you book your trip." },
    { icon: "ri-camera-off-line", title: "Wrong Passport Photo", description: "Photos from drugstores and photo booths often get rejected for lighting, background, or expression issues. We provide exact specs and catch these issues before submission." },
    { icon: "ri-file-damage-line", title: "Incomplete or Incorrect Forms", description: "One wrong date, one missing signature — your application gets sent back and you lose weeks. Our checklist catches errors before submission." },
    { icon: "ri-pen-nib-line", title: "Wrong Birth Certificate Type", description: "Not all birth certificates are accepted. Some require a long-form certified copy with a raised seal. We tell you exactly what you need." },
    { icon: "ri-calendar-close-line", title: "Assuming the Post Office is Fast Enough", description: "Standard USPS passport processing out of Lebanon runs 6–13 weeks. If your trip is in under 10 weeks, standard processing is a gamble. Use our courier service instead." },
    { icon: "ri-map-pin-time-line", title: "Name Mismatch Across Documents", description: "Your passport name must match your airline ticket exactly. Middle names, hyphens, and suffixes cause more rejections than you'd expect." },
  ],
  steps: [
    { number: "01", icon: "ri-computer-line", title: "Start Your Order Online", description: "Choose your processing speed based on your Lebanon travel date. Complete your secure order online — takes just a few minutes." },
    { number: "02", icon: "ri-file-list-3-line", title: "Receive Your Personalized Checklist", description: "We confirm your details and send you a personalized document checklist — exactly what you need, nothing more." },
    { number: "03", icon: "ri-send-plane-line", title: "Ship Documents Overnight from Lebanon", description: "Drop off at any FedEx or UPS in Lebanon or the Lebanon County area. Most locations are open evenings." },
    { number: "04", icon: "ri-building-2-line", title: "We Submit Through Reserved Access", description: "We submit your application through our reserved passport agency slots — bypassing the public appointment system." },
    { number: "05", icon: "ri-passport-line", title: "Your Passport is Completed", description: "Your passport is completed within the timeframe you selected and shipped back to you. No guessing, no hoping." },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins", "Life-or-death emergencies only", "Must travel to Philadelphia (1.75 hours)", "Expect to wait all day"],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned", "Daily status updates", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned", "Daily status updates", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Yes — limited slots, often weeks out in Philadelphia", fpc: "No appointment needed to start" },
    { feature: "In-Person Visit", agency: "Must drive to Philadelphia (1.75 hours each way)", fpc: "Ship documents overnight from Lebanon" },
    { feature: "Processing Speed", agency: "Urgent travel only — strict eligibility", fpc: "As fast as 24 hours — multiple speed options" },
    { feature: "Document Review", agency: "None — errors cause rejection", fpc: "Professional review included" },
    { feature: "Courier Access", agency: "Not available to the public", fpc: "Reserved daily submission slots" },
    { feature: "Predictability", agency: "Appointments can be cancelled", fpc: "Predictable processing based on option" },
    { feature: "Government Shutdowns", agency: "Operations may be impacted", fpc: "Not affected by government shutdowns" },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Acceptance Locations", href: "#acceptance-facilities" },
    { label: "vs. Regional Agency", href: "#comparison" },
    { label: "FAQ", href: "#faq" },
    { label: "Local Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Expedited passport services in Harrisburg", route: "/cities/harrisburg-pa" },
    { name: "Fast passport renewal in Lancaster", route: "/cities/lancaster" },
    { name: "Emergency passport help near Hershey", route: "/states/pennsylvania" },
    { name: "Rush passport service for Lebanon County travelers", route: "/states/pennsylvania" },
  ],
  fpcOfficeNote: "After signing up online, we send your personalized checklist and shipping instructions. Ship your documents overnight from any FedEx or UPS location in Lebanon or the Lebanon County area — most are open evenings. No office visit to our facility required for most services.",
  fpcOfficeDistance: "Serving Lebanon and all of Lebanon County remotely",
  serviceAreaIntro: "Whether you are in downtown Lebanon, Palmyra, Annville, Myerstown, or anywhere in Lebanon County, Fast Passport Center makes expedited passport processing simple — start online, ship overnight, and we handle the rest.",
  nearbyCitiesIntro: "We serve all Lebanon and Lebanon County zip codes. Anywhere in the Lebanon Valley — ship overnight and your documents are at our processing center the next morning.",
  howItWorksIntro: "From start to finish, we make getting an expedited passport in Lebanon as simple as possible. Most Lebanon clients never visit an office.",
  mistakesIntro: "These mistakes cause the most delays and rejections for Lebanon travelers. Fast Passport Center reviews your documents before submission to catch these issues.",
  testimonialsIntro: "Real reviews from Lebanon, Palmyra, and Annville travelers who needed their passports fast and trusted Fast Passport Center.",
  faqIntro: "The most common questions Lebanon and Lebanon County travelers ask about emergency and expedited passport services.",
  statePageRoute: "/states/pennsylvania",
  statePageName: "Pennsylvania",
  dmvWebsite: "https://www.dmv.pa.gov/",
  dmvLabel: "PennDOT — Official Website",
  vitalRecordsIcon: "ri-heart-pulse-line",
  vitalRecordsLinks: [
    { icon: "ri-user-smile-line", label: "Birth Certificate", desc: "Request certified birth certificates from Pennsylvania Department of Health Vital Records for Lebanon County.", url: "https://www.health.pa.gov/topics/certificates/Pages/Birth-Certificates.aspx" },
    { icon: "ri-file-list-2-line", label: "Death Certificate", desc: "Obtain certified death certificates through Pennsylvania Department of Health Vital Records.", url: "https://www.health.pa.gov/topics/certificates/Pages/Death-Certificates.aspx" },
    { icon: "ri-hearts-line", label: "Marriage Certificate", desc: "Request marriage license records from the Lebanon County Register of Wills.", url: "https://www.lebcounty.org/" },
    { icon: "ri-file-copy-line", label: "Divorce Decree", desc: "Obtain certified divorce records through Lebanon County Prothonotary.", url: "https://www.lebcounty.org/" },
    { icon: "ri-global-line", label: "VitalChek", desc: "Order Pennsylvania birth, death, marriage, and divorce certificates online through VitalChek for convenience.", url: "https://www.vitalchek.com/" },
  ],
};